import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";

const FeatureFlagsContext = createContext(null);

const DEFAULT_FLAGS = {
  funding_readiness_assessment: true,
  credit_report_upload: true,
  ocr_extraction: true,
  ai_credit_report_analysis: true,
  dispute_letter_generator: true,
  business_funding_module: true,
  startup_funding_module: true,
  business_formation_link: true,
  affiliate_program: true,
  admin_site_settings: true,
  // Phase F1 (Credit Upload Lockdown, IdentityIQ Exclusive).
  // Off by default — the entire manual credit-report upload UI is behind
  // this flag. Kept as a hidden switch so we can restore the upload
  // affordance later without a code round-trip. Backend does not currently
  // emit this key; the local default of `false` therefore holds unless a
  // future backend feature-flag admin surface opts in explicitly.
  manual_upload_enabled: false,
  // Phase F2 — Credit Readiness + Credit Improvement V2 defaults.
  // Backend defaults now match (see `admin_foundation.FeatureFlags`)
  // with a one-time promote for pre-existing workspaces guarded by
  // `existing[k] is False` in `seed_feature_flags`. Admins can toggle
  // OFF via the admin dashboard if a targeted rollback is required.
  credit_engine_v2: true,
  credit_analyst_v2: true,
  letter_generator_v2: true,
  // Phase G1 — Grant Navigator foundation. Backend defaults to True
  // (see `admin_foundation.FeatureFlags.grant_navigator_v1`). Client
  // mirrors so first-paint reveals the module before /feature-flags
  // resolves. Admin can toggle OFF via /admin/feature-flags; the
  // grants/grant-profile/grant-readiness routers also emit a 403
  // `feature_disabled` for defense-in-depth.
  grant_navigator_v1: true,
  // Phase G3C-A — Calendar module. Mirrors the backend default
  // (see `admin_foundation.FeatureFlags.calendar_v1`). When OFF,
  // the `/calendar` route renders the shared FeatureDisabled shell
  // and the nav pill is hidden.
  calendar_v1: true,
  // Phase G3C-B — Notifications module. Mirrors the backend
  // `notifications_v1` default. When OFF, the bell icon disappears
  // and `/notifications` renders the FeatureDisabled shell.
  notifications_v1: true,
  // Phase G3A — Business Document Vault™. Mirrors backend default
  // (see `admin_foundation.FeatureFlags.business_document_vault_v1`).
  // Missing from the client mirror caused a flash-of-disabled shell
  // before `/api/feature-flags` resolved (G4B CP2 rerun regression).
  business_document_vault_v1: true,
  // Phase G2A — Grant Match Engine + narrative LLM kill switch.
  // Client mirror for the same reason as above.
  grant_match_engine_v2a: true,
  grant_match_llm_wired: true,
  // Phase G4A — AI Grant Writer (proposals). Mirrors backend
  // default. Missing this client-side default is what caused the
  // G4B CP2 rerun P0 misroute — the `ProposalsFeatureRoute` gate
  // resolved to `false` on first render (undefined flag → falsy)
  // and the `FeatureDisabled` "Grant Writer is temporarily
  // disabled" shell rendered instead of the child `<Proposals />`
  // which would have surfaced the 402 → `PremiumUpgradePanel
  // scope="proposals"` inline. Adding this keeps the first-paint
  // state correct even before `/api/feature-flags` resolves.
  grant_writer_v1: true,
  // Phase G4B — kill switch for the proposal-section LLM call.
  // Mirrors backend default so wizard step 9 doesn't flash the
  // wrong state on cold nav.
  grant_writer_llm_wired: true,
  // Phase — Landing V2 (new brand direction preview). Default OFF so
  // `/` continues to render the existing V2.0 warm-cream landing until
  // an admin toggles this on. Fail-safe on network blip: the app keeps
  // this default (false) and the original landing renders.
  landing_v2_enabled: false,
};

/**
 * Frontend feature-flag context. Phase 4.5 only plumbs the data flow — flags
 * are read at app boot but no UI is hidden yet. Phase 8a wires the toggles.
 */
export function FeatureFlagsProvider({ children }) {
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/feature-flags")
      .then((res) => {
        if (cancelled) return;
        setFlags({ ...DEFAULT_FLAGS, ...res.data });
      })
      .catch(() => {
        // Network blip on boot — keep the safe defaults.
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      flags,
      loaded,
      isEnabled: (key) => Boolean(flags?.[key]),
    }),
    [flags, loaded],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) throw new Error("useFeatureFlags must be used inside <FeatureFlagsProvider>");
  return ctx;
}
